import { Emitter } from "../core/emitter";
import { eventsInWindow, type ScheduledEvent, type SequencerEvent } from "./events";
import { secondsPerTick } from "./time";

/** Source of the audio clock (AudioContext satisfies this). */
export interface AudioClock {
  readonly currentTime: number;
}

/** Wakes the scheduler periodically. Timing precision comes from the audio clock, not this. */
export interface WakeTimer {
  start(callback: () => void, intervalMs: number): void;
  stop(): void;
}

export interface LoopSource {
  loopTicks: number;
  events: readonly SequencerEvent[];
}

export interface Dispatch {
  event: SequencerEvent;
  timeSec: number;
  durationSec: number;
  absTick: number;
}

/** Events played once before the loop starts (e.g. count-in clicks). Ticks are negative, in [-ticks, 0). */
export interface PreRoll {
  ticks: number;
  events: readonly SequencerEvent[];
}

export interface SchedulerOptions {
  lookaheadSec: number;
  intervalMs: number;
  startDelaySec: number;
}

const DEFAULTS: SchedulerOptions = { lookaheadSec: 0.12, intervalMs: 25, startDelaySec: 0.06 };

/**
 * Lookahead scheduler ("two clocks" pattern). The wake timer only triggers
 * `tick()`; every event is dispatched with an exact AudioContext time.
 */
export class LookaheadScheduler {
  readonly dispatched = new Emitter<Dispatch>();
  readonly stateChanged = new Emitter<"playing" | "stopped">();

  private bpm = 120;
  private playing = false;
  private anchorTime = 0;
  private anchorTick = 0;
  private scheduledUpTo = 0;
  private preRoll: PreRoll | null = null;
  private readonly opts: SchedulerOptions;

  constructor(
    private clock: AudioClock,
    private timer: WakeTimer,
    private source: () => LoopSource,
    opts: Partial<SchedulerOptions> = {},
  ) {
    this.opts = { ...DEFAULTS, ...opts };
  }

  get isPlaying(): boolean { return this.playing; }
  get tempo(): number { return this.bpm; }

  /** Tempo changes re-anchor at "now" so musical position stays continuous. */
  setTempo(bpm: number): void {
    secondsPerTick(bpm); // validates
    if (this.playing) {
      const now = this.clock.currentTime;
      const tickNow = Math.max(this.tickAt(now), this.anchorTick);
      this.anchorTick = tickNow;
      this.anchorTime = Math.max(now, this.anchorTime);
    }
    this.bpm = bpm;
  }

  /** Start the transport, optionally after a pre-roll (count-in) that precedes loop tick 0. */
  start(preRoll?: PreRoll): void {
    if (this.playing) return;
    this.playing = true;
    this.preRoll = preRoll && preRoll.ticks > 0 ? preRoll : null;
    const pre = this.preRoll?.ticks ?? 0;
    this.anchorTime = this.clock.currentTime + this.opts.startDelaySec;
    this.anchorTick = -pre;
    this.scheduledUpTo = -pre;
    this.timer.start(() => this.tick(), this.opts.intervalMs);
    this.tick();
    this.stateChanged.emit("playing");
  }

  stop(): void {
    if (!this.playing) return;
    this.playing = false;
    this.timer.stop();
    this.stateChanged.emit("stopped");
  }

  /** Absolute ticks since start at an audio time (may be negative before start). */
  tickAt(timeSec: number): number {
    return this.anchorTick + (timeSec - this.anchorTime) / secondsPerTick(this.bpm);
  }

  timeAtTick(tick: number): number {
    return this.anchorTime + (tick - this.anchorTick) * secondsPerTick(this.bpm);
  }

  /**
   * Current position in ticks within the loop, for UI display. Negative
   * during a pre-roll (count-in); null when stopped.
   */
  loopPosition(): number | null {
    if (!this.playing) return null;
    const { loopTicks } = this.source();
    const t = this.tickAt(this.clock.currentTime);
    if (t < 0) return this.preRoll ? Math.max(t, -this.preRoll.ticks) : 0;
    return loopTicks <= 0 ? 0 : t % loopTicks;
  }

  /** Schedule everything up to now + lookahead. Public for deterministic tests. */
  tick(): void {
    if (!this.playing) return;
    const now = this.clock.currentTime;
    const toTick = this.tickAt(now + this.opts.lookaheadSec);
    if (toTick <= this.scheduledUpTo) return;
    const { loopTicks, events } = this.source();
    const spt = secondsPerTick(this.bpm);
    const from = this.scheduledUpTo;
    const scheduled: ScheduledEvent[] = [];
    if (from < 0 && this.preRoll) {
      const end = Math.min(toTick, 0);
      for (const ev of this.preRoll.events) if (ev.tick >= from && ev.tick < end) scheduled.push({ event: ev, absTick: ev.tick });
    }
    if (toTick > 0) scheduled.push(...eventsInWindow(events, loopTicks, Math.max(from, 0), toTick));
    for (const s of scheduled) {
      const t = this.timeAtTick(s.absTick);
      const dur = s.event.kind === "note" ? s.event.durationTicks * spt : 0;
      // Late events (e.g. after a blocked main thread) play immediately rather than in the past.
      this.dispatched.emit({ event: s.event, timeSec: Math.max(t, now), durationSec: dur, absTick: s.absTick });
    }
    this.scheduledUpTo = toTick;
  }

  dispose(): void {
    this.stop();
  }
}

/**
 * Wake timer backed by a dedicated Worker so background-tab throttling of
 * main-thread timers does not starve the scheduler. Falls back to setInterval.
 */
export class WorkerWakeTimer implements WakeTimer {
  private worker: Worker | null = null;
  private interval: ReturnType<typeof setInterval> | null = null;

  start(callback: () => void, intervalMs: number): void {
    this.stop();
    try {
      const src = `let id=null;onmessage=e=>{clearInterval(id);if(e.data>0)id=setInterval(()=>postMessage(0),e.data)}`;
      const url = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
      this.worker = new Worker(url);
      URL.revokeObjectURL(url);
      this.worker.onmessage = () => callback();
      this.worker.postMessage(intervalMs);
    } catch {
      this.worker = null;
      this.interval = setInterval(callback, intervalMs);
    }
  }

  stop(): void {
    if (this.worker) { this.worker.postMessage(0); this.worker.terminate(); this.worker = null; }
    if (this.interval !== null) { clearInterval(this.interval); this.interval = null; }
  }
}
